import {
  ROUTES,
  type AdjustStock,
  type AuthUser,
  type BulkPriceUpdate,
  type CaptchaChallenge,
  type Category,
  type CategoryTreeNode,
  type ChooseCountry,
  type Counterparty,
  type CounterpartyQueryInput,
  type CountryProfile,
  type TaxpayerLookupResult,
  type CreateCategory,
  type CreateCounterparty,
  type CreateDocument,
  type CreateProduct,
  type CreateUnit,
  type CreateUser,
  type CreateWarehouse,
  type DashboardSummary,
  type DeleteWorkspace,
  type DeleteWorkspaceResult,
  type Document,
  type DocumentEditSession,
  type DocumentPreview,
  type DocumentQueryInput,
  type DocumentSummary,
  type ForgotPasswordRequest,
  type ForgotPasswordResponse,
  type Health,
  type Locale,
  type LoginRequest,
  type LoginResponse,
  type LoginResult,
  type MovementQueryInput,
  type OnboardingSession,
  type Paginated,
  type PaginationQueryInput,
  type PasswordResetInfo,
  type PostDocument,
  type Product,
  type ProductQueryInput,
  type RegisterCompany,
  type ResetPasswordRequest,
  type Role,
  type StockItem,
  type StockMovement,
  type StockQueryInput,
  type StockValuation,
  type TenantSetting,
  type TokenPair,
  type TwoFactorStatus,
  type Unit,
  type UpdateCategory,
  type UpdateCounterparty,
  type UpdateDocument,
  type UpdateProduct,
  type UpdateUnit,
  type UpdateUser,
  type UpdateWarehouse,
  type User,
  type VerifyTwoFactor,
  type Warehouse,
  type WarehouseQueryInput,
} from '@arriba.digital/contracts';
import { http } from './client';

/**
 * Every server call the PWA can make, typed end to end from the contracts
 * package. Feature components never build a URL or a fetch by hand.
 */

/* ---------------------------------------------------------------- auth --- */

export const authApi = {
  /**
   * Step one of sign-in. Either a session comes back, or a challenge saying
   * a four-digit code was e-mailed and step two is required.
   */
  login: (payload: LoginRequest) => http.anonymousPost<LoginResult>(ROUTES.auth.login, payload),

  /** Step two: the e-mailed code. */
  verifyTwoFactor: (payload: VerifyTwoFactor) =>
    http.anonymousPost<LoginResponse>(ROUTES.auth.verifyTwoFactor, payload),

  /**
   * Self-service workspace sign-up.
   *
   * Returns a ticket rather than a session: the country is chosen on the next
   * screen, and the workspace database is built only once it is known. See
   * {@link chooseCountry}.
   */
  register: (payload: RegisterCompany) =>
    http.anonymousPost<OnboardingSession>(ROUTES.auth.register, payload),

  /** Resume a sign-up that has not chosen a country yet. */
  onboardingStatus: (ticket: string) =>
    http.onboardingGet<OnboardingSession>(ROUTES.auth.onboarding, ticket),

  /** Choose the country: this is what builds the workspace and signs in. */
  chooseCountry: (ticket: string, payload: ChooseCountry) =>
    http.onboardingPost<LoginResponse>(ROUTES.auth.chooseCountry, ticket, payload),

  /**
   * Which captcha widget to render, and its public parameters.
   *
   * `provider` is a hint, not a choice: it is only ever sent to ask for
   * Turnstile, and only from a page that cannot run the proof of work because
   * the browser withholds `crypto.subtle` outside a secure context. The
   * server ignores it unless the operator configured Turnstile.
   */
  captcha: (provider?: 'turnstile') =>
    http.anonymousGet<CaptchaChallenge>(
      provider ? `${ROUTES.auth.captcha}?provider=${provider}` : ROUTES.auth.captcha,
    ),

  forgotPassword: (payload: ForgotPasswordRequest) =>
    http.anonymousPost<ForgotPasswordResponse>(ROUTES.auth.forgotPassword, payload),

  resetPassword: (payload: ResetPasswordRequest) =>
    http.anonymousPost<{ success: true }>(ROUTES.auth.resetPassword, payload),

  /**
   * Which account a reset link belongs to.
   *
   * Called before the form is shown so the page can name the account: a link
   * that arrived for a different address is otherwise indistinguishable from
   * the right one until the password has already been changed.
   */
  passwordResetInfo: (token: string) =>
    http.anonymousPost<PasswordResetInfo>(ROUTES.auth.passwordResetInfo, {
      token,
    }),

  setTwoFactor: (enabled: boolean) =>
    http.patch<TwoFactorStatus>(ROUTES.auth.twoFactor, { enabled }),

  refresh: (refreshToken: string) =>
    http.anonymousPost<TokenPair>(ROUTES.auth.refresh, { refreshToken }),

  logout: () => http.post<{ success: boolean }>(ROUTES.auth.logout),

  me: () => http.get<AuthUser & { permissions: string[] }>(ROUTES.auth.me),

  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    http.post<{ success: boolean }>(ROUTES.auth.changePassword, payload),
};

/* -------------------------------------------------------------- system --- */

export const systemApi = {
  health: () => http.get<Health>(ROUTES.health),
};

/* ------------------------------------------------------------- catalog --- */

export const unitsApi = {
  list: (query?: PaginationQueryInput) => http.get<Paginated<Unit>>(ROUTES.units, query),
  create: (payload: CreateUnit) => http.post<Unit>(ROUTES.units, payload),
  update: (id: string, payload: UpdateUnit) => http.patch<Unit>(ROUTES.unit(id), payload),
  remove: (id: string) => http.delete<{ id: string; deleted: boolean }>(ROUTES.unit(id)),
};

export const categoriesApi = {
  list: () => http.get<Category[]>(ROUTES.categories),
  tree: () => http.get<CategoryTreeNode[]>(ROUTES.categoryTree),
  create: (payload: CreateCategory) => http.post<Category>(ROUTES.categories, payload),
  update: (id: string, payload: UpdateCategory) =>
    http.patch<Category>(ROUTES.category(id), payload),
  remove: (id: string) => http.delete<{ id: string; deleted: boolean }>(ROUTES.category(id)),
};

export const productsApi = {
  list: (query?: ProductQueryInput) => http.get<Paginated<Product>>(ROUTES.products, query),
  get: (id: string) => http.get<Product>(ROUTES.product(id)),
  stock: (id: string) =>
    http.get<
      Array<{
        warehouseId: string;
        warehouseName: string;
        warehouseCode: string;
        quantity: string;
        reserved: string;
        available: string;
        avgCost: string;
        value: string;
      }>
    >(ROUTES.productStock(id)),
  create: (payload: CreateProduct) => http.post<Product>(ROUTES.products, payload),
  update: (id: string, payload: UpdateProduct) => http.patch<Product>(ROUTES.product(id), payload),
  remove: (id: string) =>
    http.delete<{ id: string; deleted: boolean; deactivated: boolean }>(ROUTES.product(id)),
  bulkPrice: (payload: BulkPriceUpdate) =>
    http.post<{ requested: number; updated: number }>(ROUTES.productsBulkPrice, payload),
};

/* ----------------------------------------------------------- warehouses --- */

export const warehousesApi = {
  list: (query?: WarehouseQueryInput) => http.get<Paginated<Warehouse>>(ROUTES.warehouses, query),
  get: (id: string) => http.get<Warehouse>(ROUTES.warehouse(id)),
  create: (payload: CreateWarehouse) => http.post<Warehouse>(ROUTES.warehouses, payload),
  update: (id: string, payload: UpdateWarehouse) =>
    http.patch<Warehouse>(ROUTES.warehouse(id), payload),
  remove: (id: string) => http.delete<{ id: string; deleted: boolean }>(ROUTES.warehouse(id)),
};

/* ---------------------------------------------------------------- stock --- */

export const stockApi = {
  list: (query?: StockQueryInput) => http.get<Paginated<StockItem>>(ROUTES.stock, query),
  movements: (query?: MovementQueryInput) =>
    http.get<Paginated<StockMovement>>(ROUTES.movements, query),
  valuation: () => http.get<StockValuation>(ROUTES.stockValuation),
  adjust: (payload: AdjustStock) =>
    http.post<{
      changed: boolean;
      quantityBefore: string;
      quantityAfter: string;
      delta: string;
    }>(ROUTES.stockAdjust, payload),
};

/* ------------------------------------------------------------ documents --- */

export const documentsApi = {
  list: (query?: DocumentQueryInput) => http.get<Paginated<Document>>(ROUTES.documents, query),
  get: (id: string) => http.get<Document>(ROUTES.document(id)),
  summary: () => http.get<DocumentSummary>(ROUTES.documentSummary),
  preview: (id: string) => http.get<DocumentPreview>(ROUTES.documentPreview(id)),
  create: (payload: CreateDocument) => http.post<Document>(ROUTES.documents, payload),
  update: (id: string, payload: UpdateDocument) =>
    http.patch<Document>(ROUTES.document(id), payload),
  post: (id: string, payload: PostDocument = {}) =>
    http.post<Document>(ROUTES.documentPost(id), payload),
  unpost: (id: string) => http.post<Document>(ROUTES.documentUnpost(id)),
  cancel: (id: string) => http.post<Document>(ROUTES.documentCancel(id)),
  remove: (id: string) => http.delete<{ id: string; deleted: boolean }>(ROUTES.document(id)),

  /**
   * Presence: claim the draft for this user and learn who else holds it.
   *
   * Idempotent and repeated on a timer — the same call opens the lease and
   * renews it, so there is one code path whether the editor is starting or
   * still going.
   */
  startEditing: (id: string) => http.put<DocumentEditSession>(ROUTES.documentEditing(id)),

  /**
   * Release the draft. `keepalive` is what lets the call survive the page
   * being torn down on `pagehide`; without it the browser cancels it and the
   * colleague keeps seeing an editor who has left.
   */
  stopEditing: (id: string, options?: { keepalive?: boolean }) =>
    http.delete<{ released: boolean }>(ROUTES.documentEditing(id), options),
};

/* --------------------------------------------------------- counterparties - */

export const counterpartiesApi = {
  list: (query?: CounterpartyQueryInput) =>
    http.get<Paginated<Counterparty>>(ROUTES.counterparties, query),
  get: (id: string) => http.get<Counterparty>(ROUTES.counterparty(id)),
  create: (payload: CreateCounterparty) => http.post<Counterparty>(ROUTES.counterparties, payload),
  update: (id: string, payload: UpdateCounterparty) =>
    http.patch<Counterparty>(ROUTES.counterparty(id), payload),
  remove: (id: string) =>
    http.delete<{ id: string; deleted: boolean; deactivated: boolean }>(ROUTES.counterparty(id)),

  /**
   * Ask the country's registry who holds a tax identifier.
   *
   * Deliberately not a React Query `useQuery`: this is fired by a button, not
   * by rendering, and caching it in the client would only hide the fact that
   * the server already caches it per country.
   *
   * `available: false` means this deployment has no registry wired up for the
   * workspace's country — a different thing from "not found", and the two must
   * not be shown to the operator as the same message.
   */
  lookup: (taxId: string) => http.get<TaxpayerLookupResult>(ROUTES.counterpartyLookup, { taxId }),
};

/* ---------------------------------------------------------------- users --- */

export const usersApi = {
  list: (query?: PaginationQueryInput) => http.get<Paginated<User>>(ROUTES.users, query),
  create: (payload: CreateUser) => http.post<User>(ROUTES.users, payload),
  update: (id: string, payload: UpdateUser) => http.patch<User>(ROUTES.user(id), payload),
  remove: (id: string) =>
    http.delete<{ id: string; deleted: boolean; deactivated: boolean }>(ROUTES.user(id)),
};

/* ------------------------------------------------------------- settings --- */

export const settingsApi = {
  all: () => http.get<TenantSetting[]>(ROUTES.settings),
  get: (key: string) => http.get<TenantSetting>(ROUTES.setting(key)),
  update: (key: string, value: unknown) =>
    http.patch<TenantSetting>(ROUTES.setting(key), { value }),

  /**
   * The workspace's country, as the interface needs it.
   *
   * Fetched rather than compiled in: the tax rates, the identifier types, the
   * provinces and the document classes are the country's, and a workspace in
   * Chile must not be offered a CUIT. Cached for the session — a country does
   * not change while somebody is signed in.
   */
  country: () => http.get<CountryProfile>(ROUTES.settingsCountry),

  /**
   * Destroy this workspace: its database, its registry row and everything
   * cascading from it.
   *
   * Irreversible. It lives here rather than in `authApi` because the action
   * belongs to the settings screen and the password it carries is a
   * confirmation of intent, not a sign-in. The caller's session is worthless
   * the moment the server answers — the tenant row it names is gone — so the
   * client must clear it locally rather than refresh.
   */
  deleteWorkspace: (payload: DeleteWorkspace) =>
    http.post<DeleteWorkspaceResult>(ROUTES.settingsWorkspace, payload),
};

/* ------------------------------------------------------------ dashboard --- */

export const dashboardApi = {
  summary: () => http.get<DashboardSummary>(ROUTES.dashboard),
};

/* --------------------------------------------------------------- labels --- */

/** Document kind labels are i18n keys resolved by the UI. */
export const ROLE_LABEL_KEY: Record<Role, string> = {
  OWNER: 'roles.OWNER',
  ADMIN: 'roles.ADMIN',
  MANAGER: 'roles.MANAGER',
  STOREKEEPER: 'roles.STOREKEEPER',
  CASHIER: 'roles.CASHIER',
  VIEWER: 'roles.VIEWER',
};

export const LOCALE_LABEL_KEY: Record<Locale, string> = {
  en: 'locales.en',
  es: 'locales.es',
  ru: 'locales.ru',
};
